import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { IPermissionRuleEditorScope, PermissionRuleEditorCtrl } from '../controllers/PermissionRuleEditorCtrl';
import { PermissionsHelper } from '../helpers/PermissionsHelper';
import { PermissionsPanelSizeConstants } from '../helpers/PermissionsPanelResizer';
import { IPermission } from '../models/Permission';
import { PermissionRoles, SpecialRoleNames } from '../models/PermissionRoles';
import { Module } from '../Module';

Module.instance().directive('tbPermissionRuleEditor', [
      '$compile', '$q', '$timeout', 'PermissionRoles',
      ($compile: ng.ICompileService, $q: ng.IQService, $timeout: ng.ITimeoutService, permissionRoles: PermissionRoles) => {
    return {
      restrict: 'E',
      controller: PermissionRuleEditorCtrl,
      require: 'tbPermissionRuleEditor',
      scope: {
        action: '=',
        rowIndex: '=',
        updateAuthorizables: '&',
        hideRowMenu: '&',
        scrollIntoView: '&',

        // the column widths in the editable row are based on the column widths of the underlying permissions panel
        permissionsPanelColumnWidths: '=columnWidths',
        authorizables: '=',
        selectedRow: '=row',

        // used to control expand/collapse of section groups
        accordion: '='
      },
      link: (scope: IPermissionRuleEditorScope, element: JQuery, attributes: ng.IAttributes, controller: PermissionRuleEditorCtrl) => {

        scope.$watch('selectedRow', () => {
          controller.initializeFromSelectedRow();
          positionEditor();

          // The timeout is needed to let the editor render before it gets scrolled into view.
          // This is necessary because when the editor is positioned on top of the last item in the list,
          // it makes the scrollable area taller.
          $timeout(() => scope.scrollIntoView());
        });

        function positionEditor() {
          permissionRuleEditor.css({
            position: 'absolute',
            top: scope.rowIndex * PermissionsPanelSizeConstants.GridRowHeight,
            left: 0,
            right: 0,
            height: PermissionsPanelSizeConstants.GridRowHeight - 1 // 1px row border
          });
        }

        const permissionsContainer = element.parents('.tb-permissions-container');
        const explicitPermissionsScrollContentArea = permissionsContainer
          .find('.tb-explicit-permissions-container .tb-scroll-content-area');
        const effectivePermissionsScrollContentArea = permissionsContainer
          .find('.tb-effective-permissions-container .tb-scroll-content-area');

        // add backdrop to explicit permissions panel
        const explicitPermissionsBackdrop = $('<div>')
          .addClass('tb-backdrop-white').appendTo(explicitPermissionsScrollContentArea);

        // add backdrop to effective permissions panel
        const effectivePermissionsBackdrop = $('<div>')
          .addClass('tb-backdrop-white').appendTo(effectivePermissionsScrollContentArea);

        // Note: ng-include can't be on the top level dom element because it replaces itself with the template
        const permissionRuleEditor =
          $('<div>')
            .addClass('tb-permission-rule-editor')
            .append($('<div>')
              .attr({ 'ng-include': '"permissionRuleEditor.html"' }))
            .appendTo(explicitPermissionsScrollContentArea);
        $compile(permissionRuleEditor)(scope);

        scope.$on('$destroy', () => {
          permissionRuleEditor.remove();
          explicitPermissionsBackdrop.remove();
          effectivePermissionsBackdrop.remove();
        });

        // populate the options in the role dropdown.
        // TODO: cache these
        scope.roleOptions = (contentType: string) => permissionRoles.getAvailableRolesForType(contentType);

        // must be nested to avoid the dot problem
        scope.templates = {
          cells: null // initialized by permissionRuleEditor.pug
        };

        scope.isDeleteActionSelected = () => {
          return scope.action === 'delete' ||
              _.every(_.values(scope.selectedRoleByContentType), (role: string) => role === SpecialRoleNames.None);
        };

        scope.getSaveButtonText = () => {
          if (scope.isDeleteActionSelected()) {
            return Messages.deleteMessage();
          } else {
            return Messages.save();
          }
        };

        scope.savePermissionRule = () => {
          controller.savePermissionRule();
        };

        scope.getTooltipText = ($event: JQueryEventObject) => controller.getTooltipText($event);

        scope.saveDisabled = () => controller.saveDisabled();

        scope.cancel = () => {
          let maybeUpdateAuthorizables = $q.when();
          if (scope.action === 'add') {
            // the new row as already been inserted in the permissions panel, so
            // trigger a refresh to clean it up.
            maybeUpdateAuthorizables = scope.updateAuthorizables();
          }
          maybeUpdateAuthorizables.then(() => scope.hideRowMenu());
        };

        _.each(_.keys(scope.selectedRoleByContentType), (contentType: string) => {
          scope.$watch('selectedRoleByContentType' + '["' + contentType + '"]',
            (role: string) => {
              if (!role || role === SpecialRoleNames.Custom)
                return;

              scope.data.row.permissionsByContentType[contentType] = controller.getCapabilitiesForTypeAndRole(contentType, role);
            });

          // update permission role when individual permissions change
          scope.$watch('data.row.permissionsByContentType' + '["' + contentType + '"]',
            (newPermissions: _.Dictionary<IPermission>) => {
              const permissions = PermissionsHelper.PermissionsFromIPermissions(newPermissions);
              scope.selectedRoleByContentType[contentType] = permissionRoles.getExplicitRoleForTypeAndPermissions(contentType, permissions);
            }, true);
        });
      }
    };
  }
]);
